import { InputHTMLAttributes, useEffect, useState } from "react";
import { padLeadingDecimal, parseDecimal, toDisplayDecimal } from "../utils/decimal";

type DecimalInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> & {
  value: string | number | null | undefined;
  /** Receives the displayed text with a comma separator, e.g. "12,5". Use toCanonicalDecimal() for dot-stored fields. */
  onValueChange: (displayValue: string) => void;
};

/**
 * Text input for decimal numbers that always shows a comma separator: typed or pasted dots become commas,
 * and a leading separator gets a zero in front (",5" -> "0,5").
 * Keeps the typed text locally so number-backed fields can show intermediate input such as "12,".
 */
export function DecimalInput({ value, onValueChange, ...inputProps }: DecimalInputProps) {
  const [draft, setDraft] = useState(() => toDisplayDecimal(value));

  useEffect(() => {
    setDraft(current => {
      const display = toDisplayDecimal(value);
      if (current === display) return current;
      const parsed = parseDecimal(current);
      return parsed !== null && parsed === parseDecimal(value) ? current : display;
    });
  }, [value]);

  return (
    <input
      {...inputProps}
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={event => {
        const next = padLeadingDecimal(toDisplayDecimal(event.target.value));
        setDraft(next);
        onValueChange(next);
      }}
    />
  );
}
